import { z } from "zod";
import { projectIdSchema } from "./schemas.ts";
import { contextItemSchema } from "./context.ts";

/** Normalized reasoning effort. Providers translate it; models list only the values they really support. */
export const EFFORTS = ["low", "medium", "high"] as const;
export const effortSchema = z.enum(EFFORTS);
export type Effort = z.infer<typeof effortSchema>;

/** Agent modes are tool-permission policies. chat is non-mutating; agent performs development work; ask|plan|edit are legacy compatibility mappings. */
export const AI_MODES = ["chat", "agent", "ask", "plan", "edit"] as const;
export const aiModeSchema = z.enum(AI_MODES);
export type AiMode = z.infer<typeof aiModeSchema>;

export const AGENT_BEHAVIORS = ["interactive", "autonomous"] as const;
export const agentBehaviorSchema = z.enum(AGENT_BEHAVIORS);
export type AgentBehavior = z.infer<typeof agentBehaviorSchema>;

/** Small, explicit capability/effect classification for the Autonomous Development Scope. */
export const ACTION_EFFECTS = [
  "DEVELOPMENT_SCOPE",
  "OUT_OF_SCOPE",
  "CRITICAL_DENY",
  "UNKNOWN",
] as const;
export const actionEffectSchema = z.enum(ACTION_EFFECTS);
export type ActionEffect = z.infer<typeof actionEffectSchema>;

export const POLICY_DECISIONS = ["auto", "approval", "block", "deny"] as const;
export const policyDecisionSchema = z.enum(POLICY_DECISIONS);
export type PolicyDecision = z.infer<typeof policyDecisionSchema>;

export const dodItemSchema = z.strictObject({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(100),
  command: z.strictObject({
    executable: z.string().min(1),
    args: z.array(z.string()),
    cwd: z.string().optional(),
  }),
  required: z.boolean(),
});
export type DoDItem = z.infer<typeof dodItemSchema>;

export const runCeilingsSchema = z.strictObject({
  maxRuntimeMinutes: z.number().int().positive().default(30),
  maxModelCalls: z.number().int().positive().default(20),
  maxAutoChanges: z.number().int().positive().default(20),
  maxAutoActions: z.number().int().positive().default(40),
});
export type RunCeilings = z.infer<typeof runCeilingsSchema>;

export const providerIdSchema = z.string().regex(/^[a-z0-9][a-z0-9_-]{0,63}$/);
export const modelIdSchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[\w.:/@+-]+$/);
export const uuidSchema = z.uuid();

import { modelPricingSchema } from "./pricing.ts";

export const modelDescriptorSchema = z.strictObject({
  id: modelIdSchema,
  name: z.string().max(200),
  contextWindow: z.number().int().positive().optional(),
  /** Supported effort levels. Empty = the model has no effort control; the UI must not offer one. */
  efforts: z.array(effortSchema),
  /** USD per million tokens, only when the provider publishes it. Never hard-coded by Racler. */
  pricing: modelPricingSchema.optional(),
  supportsTextAttachments: z.boolean().optional(),
  supportsImageInput: z.boolean().optional(),
  supportsPdfInput: z.boolean().optional(),
});
export type ModelDescriptor = z.infer<typeof modelDescriptorSchema>;

export const providerKindSchema = z.enum(["openai-compatible", "anthropic", "gemini", "mock"]);
export type ProviderKind = z.infer<typeof providerKindSchema>;

export const providerInfoSchema = z.strictObject({
  id: providerIdSchema,
  name: z.string().max(100),
  kind: providerKindSchema,
  /** True when a credential exists locally. The credential itself is never transmitted. */
  configured: z.boolean(),
  models: z.array(modelDescriptorSchema).max(3000),
  error: z.string().max(300).optional(),
});
export type ProviderInfo = z.infer<typeof providerInfoSchema>;

export const usageSchema = z.strictObject({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedTokens: z.number().int().nonnegative().optional(),
  reasoningTokens: z.number().int().nonnegative().optional(),
  /** Cost as reported by the provider (e.g. OpenRouter). Not computed locally. */
  costUsd: z.number().nonnegative().optional(),
  /** Optional non-invasive discriminator (e.g. 'autocomplete'). */
  kind: z.string().optional(),
});
export type Usage = z.infer<typeof usageSchema>;

/** Tool arguments as shown/persisted: flat, short values only (full file contents never travel here). */
export const toolArgsSchema = z
  .record(z.string().max(64), z.union([z.string().max(300), z.number(), z.boolean(), z.null()]))
  .refine((o) => Object.keys(o).length <= 12, "too many arguments");

export type ToolArgs = z.infer<typeof toolArgsSchema>;

export const toolActivitySchema = z.strictObject({
  toolCallId: z.string().min(1).max(128),
  name: z.string().min(1).max(64),
  args: toolArgsSchema,
  iteration: z.number().int().nonnegative(),
  /** null while the tool is running. */
  ok: z.boolean().nullable(),
  errorCode: z.string().max(64).optional(),
  summary: z.string().max(300).optional(),
  durationMs: z.number().int().nonnegative().optional(),
});
export type ToolActivity = z.infer<typeof toolActivitySchema>;

/** `awaiting_approval`: the run paused at a human approval point; `blocked_by_policy`: autonomous run stopped at policy boundary. */
export const STOP_REASONS = [
  "end",
  "cancelled",
  "max_iterations",
  "length",
  "awaiting_approval",
  "blocked_by_policy",
] as const;
export const AI_ERROR_CODES = [
  "auth",
  "rate_limit",
  "bad_request",
  "network",
  "server",
  "unsupported",
  "not_configured",
  "unknown_model",
  "agent_disconnected",
  "interrupted",
  "internal",
] as const;
export type AiErrorCode = (typeof AI_ERROR_CODES)[number];

export const chatMessageSchema = z.strictObject({
  id: uuidSchema,
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  status: z.enum(["complete", "running", "error", "cancelled"]),
  activity: z.array(toolActivitySchema),
  provider: z.string().nullable(),
  model: z.string().nullable(),
  usage: usageSchema.nullable(),
  stopReason: z.string().nullable(),
  error: z.string().nullable(),
  runId: uuidSchema.nullable(),
  contextItems: z.array(contextItemSchema).optional(),
  createdAt: z.string(),
});
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const lastModelSchema = z.strictObject({
  provider: providerIdSchema,
  model: modelIdSchema,
  effort: effortSchema.optional(),
});
export type LastModel = z.infer<typeof lastModelSchema>;

export const chatSessionSchema = z.strictObject({
  id: uuidSchema,
  projectId: projectIdSchema,
  title: z.string(),
  mode: aiModeSchema,
  provider: z.string(),
  model: z.string(),
  effort: effortSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ChatSession = z.infer<typeof chatSessionSchema>;

/** M10A: Durable run lifecycle states. */
export const RUN_LIFECYCLE_STATUSES = [
  "queued",
  "running",
  "awaiting_approval",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
] as const;
export const runLifecycleStatusSchema = z.enum(RUN_LIFECYCLE_STATUSES);
export type RunLifecycleStatus = z.infer<typeof runLifecycleStatusSchema>;

/** M10A: Structured timeline event stored with the durable run. */
export const durableRunEventSchema = z.strictObject({
  id: z.string(),
  type: z.string(),
  timestamp: z.string(),
  stage: z.string().optional(),
  summary: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
});
export type DurableRunEvent = z.infer<typeof durableRunEventSchema>;

/** M10A: Canonical persisted run record. */
export const agentRunRecordSchema = z.strictObject({
  id: uuidSchema,
  userId: uuidSchema,
  machineId: uuidSchema,
  projectId: projectIdSchema,
  chatSessionId: uuidSchema,
  messageId: uuidSchema,
  mode: aiModeSchema,
  provider: z.string(),
  model: z.string(),
  status: runLifecycleStatusSchema,
  stage: z.string(),
  error: z.string().nullable(),
  environmentId: uuidSchema.nullable().optional(),
  createdAt: z.string(),
  startedAt: z.string(),
  updatedAt: z.string(),
  finishedAt: z.string().nullable(),
  events: z.array(durableRunEventSchema).default([]),
});
export type AgentRunRecord = z.infer<typeof agentRunRecordSchema>;

